import React, { useState } from 'react';
import { CareerFocusWithMetrics } from '../../hooks/useCareerFocus';
import { useGoals, GoalType, FocusGoal } from '../../hooks/useGoals';
import { useDarkMode } from '../../context/DarkModeContext';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  IoAddOutline, 
  IoCheckmarkCircleOutline, 
  IoTrophyOutline,
  IoSchoolOutline,
  IoPeopleOutline,
  IoBriefcaseOutline,
  IoEllipsisHorizontalOutline,
  IoPencilOutline,
  IoTrashOutline,
  IoReorderFourOutline
} from 'react-icons/io5';
import MappingModal from '../Mapping/MappingModal';
import { useGoalMappedCategories } from '../../hooks/useGoalMappedCategories';

interface MyGoalsTabProps {
  focus: CareerFocusWithMetrics;
  goals: FocusGoal[];
  goalsLoading: boolean;
  goalsError: Error | null;
  refreshGoals: () => Promise<void>;
  activeRubric?: any; // Rubric data from parent
  onQuickAdd?: (goalId?: string, goalText?: string) => void;
}

const goalTypeConfig: Record<GoalType, { icon: React.ReactNode; label: string; color: string; defaultTarget: number }> = {
  return_offer: { 
    icon: <IoTrophyOutline className="w-5 h-5" />, 
    label: 'Return Offer',
    color: 'yellow',
    defaultTarget: 8
  },
  skill_learning: { 
    icon: <IoSchoolOutline className="w-5 h-5" />, 
    label: 'Skill Learning',
    color: 'blue',
    defaultTarget: 5
  },
  networking: { 
    icon: <IoPeopleOutline className="w-5 h-5" />, 
    label: 'Networking',
    color: 'green',
    defaultTarget: 3
  },
  portfolio: { 
    icon: <IoBriefcaseOutline className="w-5 h-5" />, 
    label: 'Portfolio',
    color: 'purple',
    defaultTarget: 10
  },
  custom: { 
    icon: <IoEllipsisHorizontalOutline className="w-5 h-5" />, 
    label: 'Custom',
    color: 'gray',
    defaultTarget: 5
  }
};

const MyGoalsTab: React.FC<MyGoalsTabProps> = ({ 
  focus, 
  goals, 
  goalsLoading, 
  goalsError, 
  refreshGoals,
  activeRubric,
  onQuickAdd
}) => {
  const { darkMode } = useDarkMode();
  // We still need the operations from useGoals hook
  const { createGoal, updateGoal, deleteGoal } = useGoals(focus.id);
  const { mappedCategories } = useGoalMappedCategories(focus.id, activeRubric?.id);
  const [isAddingGoal, setIsAddingGoal] = useState(false);
  const [editingGoalId, setEditingGoalId] = useState<string | null>(null);
  const [newGoalText, setNewGoalText] = useState('');
  const [newGoalType, setNewGoalType] = useState<GoalType>('custom');
  const [newGoalTarget, setNewGoalTarget] = useState(5); // Default evidence target
  const [showMappingModal, setShowMappingModal] = useState(false);
  
  // Edit mode state
  const [editText, setEditText] = useState('');
  const [editTarget, setEditTarget] = useState(5);

  const textColor = darkMode ? 'text-gray-100' : 'text-gray-900';
  const mutedTextColor = darkMode ? 'text-gray-400' : 'text-gray-600';
  const cardBg = darkMode ? 'bg-gray-700' : 'bg-gray-50';
  const borderColor = darkMode ? 'border-gray-600' : 'border-gray-200';
  const inputBg = darkMode ? 'bg-gray-800' : 'bg-white';

  const getColorClasses = (color: string) => {
    const colors: Record<string, string> = {
      yellow: darkMode ? 'bg-yellow-900/20 text-yellow-400' : 'bg-yellow-100 text-yellow-700',
      blue: darkMode ? 'bg-blue-900/20 text-blue-400' : 'bg-blue-100 text-blue-700',
      green: darkMode ? 'bg-green-900/20 text-green-400' : 'bg-green-100 text-green-700',
      purple: darkMode ? 'bg-purple-900/20 text-purple-400' : 'bg-purple-100 text-purple-700',
      gray: darkMode ? 'bg-gray-700 text-gray-400' : 'bg-gray-200 text-gray-700'
    };
    return colors[color] || colors.gray;
  };

  const handleCreateGoal = async () => {
    if (!newGoalText.trim()) return;

    const success = await createGoal({
      focus_id: focus.id,
      goal_text: newGoalText,
      goal_type: newGoalType,
      priority: goals.length + 1,
      evidence_target: newGoalTarget
    });

    if (success) {
      await refreshGoals(); // Refresh goals after creation
      setNewGoalText('');
      setNewGoalType('custom');
      setNewGoalTarget(5);
      setIsAddingGoal(false);
    }
  };

  // Edit mode handlers
  const handleEditStart = (goal: FocusGoal) => {
    setEditingGoalId(goal.id);
    setEditText(goal.goal_text);
    setEditTarget(goal.evidence_target || 5);
  };

  const handleEditCancel = () => {
    setEditingGoalId(null);
    setEditText('');
    setEditTarget(5);
  };

  const handleEditSave = async (goalId: string) => {
    if (!editText.trim()) return;

    const success = await updateGoal(goalId, {
      goal_text: editText,
      evidence_target: editTarget
    });

    if (success) {
      await refreshGoals(); // Refresh goals after update
      handleEditCancel();
    }
  };

  if (goalsLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-500"></div>
      </div>
    );
  }
  
  if (goalsError) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-red-500 text-center">
          <p className="mb-2">Failed to load goals</p>
          <button 
            onClick={() => refreshGoals()}
            className="text-sm text-blue-500 hover:text-blue-600"
          >
            Try again
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {/* Header */}
      <div className="flex items-center justify-between mb-4">
        <div>
          <h3 className={`text-lg font-semibold ${textColor}`}>Personal Goals</h3>
          <p className={`text-sm ${mutedTextColor}`}>
            What you want to achieve during your internship
          </p>
        </div>
        {!isAddingGoal && (
          <button
            onClick={() => setIsAddingGoal(true)}
            className="flex items-center gap-2 px-3 py-1.5 bg-blue-500 hover:bg-blue-600 text-white rounded-lg text-sm font-medium transition-colors"
          >
            <IoAddOutline className="w-4 h-4" />
            Add Goal
          </button>
        )}
      </div>

      {/* Add Goal Form */}
      <AnimatePresence>
        {isAddingGoal && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: 'auto' }}
            exit={{ opacity: 0, height: 0 }}
            className={`p-4 rounded-lg border ${borderColor} ${cardBg} mb-4`}
          >
            <div className="space-y-3">
              <div>
                <label className={`block text-sm font-medium ${textColor} mb-1`}>
                  Goal Type
                </label>
                <div className="flex gap-2 flex-wrap">
                  {Object.entries(goalTypeConfig).map(([type, config]) => (
                    <button
                      key={type}
                      onClick={() => {
                        setNewGoalType(type as GoalType);
                        setNewGoalTarget(config.defaultTarget);
                      }}
                      className={`px-3 py-1.5 rounded-lg text-sm font-medium transition-colors flex items-center gap-2 ${
                        newGoalType === type
                          ? getColorClasses(config.color)
                          : darkMode ? 'bg-gray-700 text-gray-400' : 'bg-gray-200 text-gray-600'
                      }`}
                    >
                      {config.icon}
                      {config.label}
                    </button>
                  ))}
                </div>
              </div>

              <div>
                <label className={`block text-sm font-medium ${textColor} mb-1`}>
                  Goal Description
                </label>
                <textarea
                  value={newGoalText}
                  onChange={(e) => setNewGoalText(e.target.value)}
                  placeholder="E.g., Get a return offer for full-time position"
                  className={`w-full px-3 py-2 rounded-lg border ${borderColor} ${inputBg} ${textColor} placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-blue-500`}
                  rows={2}
                />
              </div>

              <div>
                <label className={`block text-sm font-medium ${textColor} mb-1`}>
                  Evidence Target
                  <span className={`ml-2 text-xs ${mutedTextColor}`}>
                    (pieces of evidence for 100% completion)
                  </span>
                </label>
                <input
                  type="number"
                  min="1"
                  max="20"
                  value={newGoalTarget}
                  onChange={(e) => setNewGoalTarget(Math.max(1, Math.min(20, parseInt(e.target.value) || 1)))}
                  className={`w-24 px-3 py-2 rounded-lg border ${borderColor} ${inputBg} ${textColor} focus:outline-none focus:ring-2 focus:ring-blue-500`}
                />
              </div>

              <div className="flex gap-2 justify-end">
                <button
                  onClick={() => {
                    setIsAddingGoal(false);
                    setNewGoalText('');
                    setNewGoalType('custom');
                    setNewGoalTarget(5);
                  }}
                  className={`px-3 py-1.5 rounded-lg text-sm font-medium ${
                    darkMode ? 'bg-gray-700 text-gray-300' : 'bg-gray-200 text-gray-700'
                  }`}
                >
                  Cancel
                </button>
                <button
                  onClick={handleCreateGoal}
                  disabled={!newGoalText.trim()}
                  className={`px-3 py-1.5 rounded-lg text-sm font-medium transition-colors ${
                    newGoalText.trim()
                      ? 'bg-blue-500 hover:bg-blue-600 text-white'
                      : 'bg-gray-300 text-gray-500 cursor-not-allowed'
                  }`}
                >
                  Add Goal
                </button>
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Goals List */}
      {goals.length === 0 ? (
        <div className={`text-center py-8 ${mutedTextColor}`}>
          <IoTrophyOutline className="w-12 h-12 mx-auto mb-3 opacity-50" />
          <p className="text-sm">No goals yet. Add your first goal to get started!</p>
        </div>
      ) : (
        <div className="space-y-3">
          {goals.map((goal) => {
            const config = goalTypeConfig[goal.goal_type];
            const mappingCount = goal.rubric_mappings?.length || 0;
            const evidenceCount = goal.evidence_count || 0;
            const evidenceTarget = goal.evidence_target || 5; // Use goal's target or default to 5
            const progressPercentage = Math.min((evidenceCount / evidenceTarget) * 100, 100);

            // Goal emoji icons matching mockup
            const goalEmojis = {
              return_offer: '🎯',
              skill_learning: '📚',
              networking: '🤝',
              portfolio: '💼',
              custom: '🎯'
            };

            return (
              <motion.div
                key={goal.id}
                layout
                className={`p-5 rounded-lg border ${borderColor} ${darkMode ? 'bg-gray-900' : 'bg-gray-50'} transition-all hover:border-blue-500 ${
                  editingGoalId && editingGoalId !== goal.id ? 'opacity-50' : ''
                }`}
              >
                <div className="flex items-start gap-3">
                  {/* Goal Icon */}
                  <span className="text-2xl">{goalEmojis[goal.goal_type]}</span>

                  {/* Goal Content */}
                  <div className="flex-1 min-w-0">
                    {editingGoalId === goal.id ? (
                      <div className="space-y-3">
                        <textarea
                          value={editText}
                          onChange={(e) => setEditText(e.target.value)}
                          className={`w-full px-3 py-2 rounded-lg border ${borderColor} ${inputBg} ${textColor} resize-none focus:outline-none focus:ring-2 focus:ring-blue-500`}
                          rows={2}
                          autoFocus
                          onKeyDown={(e) => {
                            if (e.key === 'Enter' && !e.shiftKey) {
                              e.preventDefault();
                              handleEditSave(goal.id);
                            } else if (e.key === 'Escape') {
                              handleEditCancel();
                            }
                          }}
                        />
                        <div className="flex items-center gap-4">
                          <div className="flex items-center gap-2">
                            <label className={`text-sm ${textColor}`}>Evidence Target:</label>
                            <input
                              type="number"
                              min="1"
                              max="20"
                              value={editTarget}
                              onChange={(e) => setEditTarget(Math.max(1, Math.min(20, parseInt(e.target.value) || 1)))}
                              className={`w-16 px-2 py-1 rounded border ${borderColor} ${inputBg} ${textColor} text-sm focus:outline-none focus:ring-2 focus:ring-blue-500`}
                            />
                            <span className={`text-sm ${mutedTextColor}`}>pieces</span>
                          </div>
                          <div className="flex gap-2 ml-auto">
                            <button
                              onClick={handleEditCancel}
                              className={`px-3 py-1 rounded text-sm ${darkMode ? 'bg-gray-700 text-gray-300' : 'bg-gray-200 text-gray-700'}`}
                            >
                              Cancel
                            </button>
                            <button
                              onClick={() => handleEditSave(goal.id)}
                              disabled={!editText.trim()}
                              className={`px-3 py-1 rounded text-sm ${
                                editText.trim()
                                  ? 'bg-blue-500 hover:bg-blue-600 text-white'
                                  : 'bg-gray-300 text-gray-500 cursor-not-allowed'
                              }`}
                            >
                              Save
                            </button>
                          </div>
                        </div>
                      </div>
                    ) : (
                      <div className="flex items-center justify-between gap-2">
                        <h4 className={`font-medium ${textColor} text-base`}>
                          {goal.goal_text}
                        </h4>
                        <div className="flex items-center gap-2">
                          {onQuickAdd && (
                            <button
                              onClick={() => onQuickAdd(goal.id, goal.goal_text)}
                              className={`px-3 py-1 rounded-md text-sm font-medium transition-colors flex items-center gap-1 ${
                                darkMode 
                                  ? 'bg-gray-700 hover:bg-gray-600 text-gray-300' 
                                  : 'bg-gray-100 hover:bg-gray-200 text-gray-700'
                              }`}
                              title="Add evidence for this goal"
                            >
                              <IoAddOutline className="w-3 h-3" />
                              Add Evidence
                            </button>
                          )}
                          <button
                            onClick={() => handleEditStart(goal)}
                            className={`${mutedTextColor} hover:text-blue-500 p-1 rounded transition-colors`}
                            title="Edit goal"
                          >
                            <IoPencilOutline className="w-4 h-4" />
                          </button>
                          <button
                            className={`${mutedTextColor} hover:text-gray-400`}
                            title="More options"
                          >
                            ▼
                          </button>
                        </div>
                      </div>
                    )}

                    {/* Meta Information */}
                    <div className="mt-2 space-y-2">
                      <div className={`flex items-center gap-2 text-sm ${mutedTextColor}`}>
                        <span>📋</span>
                        <span>
                          Maps to: {
                            mappedCategories[goal.id]?.length > 0 
                              ? mappedCategories[goal.id].join(', ')
                              : 'Not mapped yet'
                          }
                        </span>
                      </div>
                      <div className={`flex items-center gap-2 text-sm ${mutedTextColor}`}>
                        <span>📄</span>
                        <span>Evidence: {goal.career_card_count || 0} career cards, {goal.note_count || 0} notes</span>
                      </div>
                    </div>

                    {/* Progress */}
                    <div className="mt-3 flex items-center gap-3">
                      <div className={`flex-1 h-2 rounded ${darkMode ? 'bg-gray-700' : 'bg-gray-200'} overflow-hidden`}>
                        <div 
                          className="h-full bg-green-500 transition-all duration-300"
                          style={{ width: `${progressPercentage}%` }}
                        />
                      </div>
                      <span className={`text-sm ${mutedTextColor} min-w-[60px] text-right`}>
                        {evidenceCount}/{evidenceTarget} ({Math.round(progressPercentage)}%)
                      </span>
                    </div>
                  </div>
                </div>
              </motion.div>
            );
          })}
        </div>
      )}

      {/* Add Another Goal Button */}
      {goals.length > 0 && !isAddingGoal && (
        <button
          onClick={() => setIsAddingGoal(true)}
          className={`w-full p-4 rounded-lg border-2 border-dashed ${borderColor} ${mutedTextColor} hover:border-blue-500 hover:text-blue-500 transition-colors text-sm font-medium`}
        >
          + Add Another Goal
        </button>
      )}

      {/* View Goal-Rubric Mapping Button */}
      <div className="mt-5 text-center">
        <button
          onClick={() => setShowMappingModal(true)}
          className={`inline-flex items-center gap-2 px-4 py-2 rounded-lg ${darkMode ? 'bg-gray-700 hover:bg-gray-600' : 'bg-gray-200 hover:bg-gray-300'} ${textColor} transition-colors text-sm font-medium`}
        >
          <span>🔗</span>
          <span>View Goal-Rubric Mapping</span>
        </button>
      </div>

      {/* Goal-Rubric Mapping Modal */}
      <MappingModal
        focusId={focus.id}
        isOpen={showMappingModal}
        onClose={() => setShowMappingModal(false)}
      />
    </div>
  );
};

export default MyGoalsTab;